import type { Order, OrderItem } from '../graphql/query/getOrder';

/**
 * Finds the tracking number for a given item ID by looking up the shipment it belongs to.
 * This is a clean, testable utility that handles the mapping from item ID to tracking number.
 */
export const getTrackingNumberForItem = (
  order: Order,
  itemId: string,
): string | undefined => {
  if (!order.shipments) return undefined;

  // Find the shipment that contains this item
  const shipment = order.shipments.find((shipment) =>
    shipment.itemsInfo?.some((itemInfo) => itemInfo.itemId === itemId),
  );

  return shipment?.trackingNumber || undefined;
};

/**
 * Gets the tracking number for an item from a list of shipment groups.
 * This is used when we already have grouped shipment data.
 *
 * If the item ID is not found directly, it tries to find the item by SKU
 * to handle cases where virtual items have different IDs than the original items.
 */
export const getTrackingNumberFromGroups = (
  shipmentGroups: Array<{ trackingNumber: string; items: OrderItem[] }>,
  itemId: string,
  originalItem?: OrderItem,
): string | undefined => {
  // First try to find by exact item ID
  const group = shipmentGroups.find((group) =>
    group.items.some((item) => item.id === itemId),
  );

  if (group) {
    return group.trackingNumber;
  }

  // If not found and we have the original item, try to find by SKU
  if (originalItem?.sku) {
    const matchingGroup = shipmentGroups.find((group) =>
      group.items.some((item) => item.sku === originalItem.sku),
    );

    if (matchingGroup) {
      return matchingGroup.trackingNumber;
    }
  }

  return undefined;
};
